<template>
  <div class="be-room-card__detail">
    <div class="be-room-card__detail-tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="be-room-card__detail-tab"
        :class="{ 'is-active': tab.key === activeTab }"
        @click="activeTab = tab.key">
        {{ tab.label }}
      </button>
    </div>

    <div class="be-room-card__detail-panel">
      <template v-if="activeTab === 'description'">
        <p v-if="$isNotEmpty(rate.description_room_rate)" class="be-room-card__detail-text" v-html="$convertNewlinesToBr(rate.description_room_rate)"></p>
        <p v-if="$isNotEmpty(rate.description_promotion)" class="be-room-card__detail-text" v-html="$convertNewlinesToBr(rate.description_promotion)"></p>
        <p v-if="!hasDescription" class="be-room-card__detail-text be-room-card__detail-empty">–</p>
      </template>

      <template v-if="activeTab === 'terms'">
        <div v-if="$isNotEmpty(rate.term_and_condition_room_rate)" class="be-room-card__detail-block">
          <p class="be-room-card__detail-title">{{ rate.term_and_condition_room_rate.title }}</p>
          <div class="be-room-card__detail-text html-desc" v-html="rate.term_and_condition_room_rate.description"></div>
        </div>
        <div v-if="$isNotEmpty(rate.term_and_condition_promotion)" class="be-room-card__detail-block">
          <p class="be-room-card__detail-title">{{ rate.term_and_condition_promotion.title }}</p>
          <div class="be-room-card__detail-text html-desc" v-html="rate.term_and_condition_promotion.description"></div>
        </div>
      </template>

      <template v-if="activeTab === 'cancellation'">
        <p class="be-room-card__detail-title">{{ rate.cancellation_policy.title }}</p>
        <div class="be-room-card__detail-text html-desc" v-html="rate.cancellation_policy.description"></div>
      </template>

      <template v-if="activeTab === 'inclusions'">
        <ul class="be-room-card__detail-inclusions">
          <li v-for="(inclusion, i) in rate.rate.inclusion" :key="i">
            <span class="be-room-card__detail-title">{{ inclusion.inclusion_item.name }}</span>
            <span class="be-room-card__detail-text restrict-p-mb html-desc" v-html="inclusion.inclusion_item.description"></span>
          </li>
        </ul>
      </template>

      <template v-if="activeTab === 'breakdown'">
        <div class="be-room-card__detail-row" v-for="(breakdownRow, i) in breakdownRows" :key="i">
          <span class="be-room-card__detail-text">{{ breakdownRow.date }}</span>
          <span class="be-room-card__detail-text be-room-card__detail-amount">{{ breakdownRow.price }}</span>
        </div>
        <div class="be-room-card__detail-row be-room-card__detail-row--total" v-if="avail">
          <span class="be-room-card__detail-title">{{ $t('hotel.rate-breakdown.full-rates') }}</span>
          <span class="be-room-card__detail-title be-room-card__detail-amount">{{ totalPriceText }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { formatCurrency } from '@/utils/index.js';
  import { getTotalPriceFromDate, printTotalPriceWithTax } from './pricing.js';

  export default {
    name: 'RoomCardRateDetail',
    props: {
      rate: {
        type: Object,
        default: () => ({})
      },
      avail: {
        type: Number,
        default: 0
      }
    },
    data(){
      return {
        activeTab: 'description'
      };
    },
    computed: {
      isShowTax(){
        return this.getMerchant().search_room_is_show_tax_fee_note;
      },
      hasDescription(){
        return this.$isNotEmpty(this.rate.description_room_rate) || this.$isNotEmpty(this.rate.description_promotion);
      },
      hasTerms(){
        return this.$isNotEmpty(this.rate.term_and_condition_room_rate) || this.$isNotEmpty(this.rate.term_and_condition_promotion);
      },
      hasCancellationPolicy(){
        return this.$isNotEmptyV2(this.rate.cancellation_policy);
      },
      hasInclusions(){
        const inclusions = (this.rate.rate && this.rate.rate.inclusion) || [];
        return inclusions.length > 0;
      },
      showBreakdown(){
        if(!this.rate.bookable || this.avail <= 0) return false;
        const { search_room_is_show_rate_package_daily_breakdown: configMerchant = false } = this.getMerchant();
        return configMerchant ? true : !this.rate.rate.is_package;
      },
      tabs(){
        const tabs = [{ key: 'description', label: this.$t('general.description') }];
        if(this.hasTerms) tabs.push({ key: 'terms', label: this.$t('merchant.term-condition') });
        if(this.hasCancellationPolicy) tabs.push({ key: 'cancellation', label: this.rate.cancellation_policy.title });
        if(this.hasInclusions) tabs.push({ key: 'inclusions', label: this.$t('hotel.room-type.include-in-rate') });
        if(this.showBreakdown) tabs.push({ key: 'breakdown', label: this.$t('hotel.rate-breakdown.title') });
        return tabs;
      },
      breakdownRows(){
        return this.rate.dates.map(date => ({
          date: this.$formatDate(date.date),
          price: formatCurrency(printTotalPriceWithTax(date.final_price, date.taxes, this.isShowTax))
        }));
      },
      totalPriceText(){
        return formatCurrency(getTotalPriceFromDate(this.rate.dates, 'final_price', this.isShowTax, true));
      }
    },
    methods: {
      ...mapGetters(['getMerchant'])
    }
  }
</script>

<style scoped>
.be-room-card__detail {
  grid-column: 1 / -1;
  padding: 14px 0 16px;
  border-top: 1px solid var(--be-v3-border-soft, #e3e6eb);
}
.be-room-card__detail-tabs {
  display: flex;
  align-items: center;
  gap: 28px;
  overflow-x: auto;
  border-bottom: 1px solid var(--be-v3-border-soft, #e3e6eb);
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.be-room-card__detail-tabs::-webkit-scrollbar {
  display: none;
}
.be-room-card__detail-tab {
  flex-shrink: 0;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0 0 10px;
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  line-height: 1.4;
  color: var(--merchant-primary-color, #4a7c59);
  cursor: pointer;
  white-space: nowrap;
}
.be-room-card__detail-tab:focus {
  outline: none;
}
.be-room-card__detail-tab.is-active {
  color: #1a1a2e;
  border-bottom-color: #1a1a2e;
}
.be-room-card__detail-panel {
  padding-top: 14px;
}
.be-room-card__detail-block + .be-room-card__detail-block {
  margin-top: 14px;
}
.be-room-card__detail-title {
  display: block;
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  font-weight: 700;
  line-height: 1.4;
  color: #1a1a2e;
  margin: 0 0 4px;
}
.be-room-card__detail-text {
  font-size: var(--be-v3-text-secondary, 0.8125rem);
  line-height: 1.5;
  color: #333;
  margin: 0;
}
.be-room-card__detail-text + .be-room-card__detail-text {
  margin-top: 8px;
}
.be-room-card__detail-empty {
  color: #6d6d6d;
}
.be-room-card__detail-inclusions {
  list-style: disc;
  padding-left: 18px;
  margin: 0;
}
.be-room-card__detail-inclusions li {
  margin-bottom: 8px;
}
.be-room-card__detail-inclusions li:last-child {
  margin-bottom: 0;
}
.be-room-card__detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;
}
.be-room-card__detail-row--total {
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid var(--be-v3-border-soft, #e3e6eb);
}
.be-room-card__detail-amount {
  font-weight: 600;
}
@media screen and (min-width: 992px) {
  .be-room-card__detail {
    padding: 16px 16px 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/room-card/RoomCardRateDetail.vue